import { useRef, useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { authApi } from "../redux/services/auth";
import { setAccessToken } from "../redux/slices/user.slice";

let baseUrl = process.env.BACKEND_URL;
baseUrl = baseUrl.replace("api", "ws");
baseUrl = baseUrl.replace("https", "wss");
baseUrl = baseUrl.replace("http", "ws");

export const useWS = ({
  token = null,
  stationId = null,
  skipAuth = false,
} = {}) => {
  const ws = useRef(null);
  const pingIntervalId = useRef(null);
  const reconnectTimeoutId = useRef(null);
  const reconnectDelay = useRef(1000);
  const shouldReconnect = useRef(true);
  const latestSocketUrl = useRef(null);

  const [socketInstance, setSocketInstance] = useState(null);
  const [station, setStation] = useState(stationId);

  const dispatch = useDispatch();
  dispatch(setAccessToken(token));
  const access_token = useSelector((state) => state.user.access_token);

  useEffect(() => {
    if (token && !access_token) {
      dispatch(setAccessToken(token));
    }
  }, [token]);

  useEffect(() => {
    let url;
    if (!skipAuth && access_token && station) {
      url = `${baseUrl}/?token=${access_token}&station=${station}`;
    }
    else if (!skipAuth && access_token) {
      url = `${baseUrl}/?token=${access_token}`;
    }
    else {
      url = baseUrl;
    }

    latestSocketUrl.current = url;

    if (!ws.current) {
      connect(url);
    }
  }, [access_token, station, skipAuth, connect]);

  const reconnectWithNewToken = useCallback(async () => {
    console.warn("🔄 Attempting token refresh...");
    try {
      const result = await api
        .dispatch(authApi.endpoints.refreshToken.initiate())
        .unwrap();
      const newToken = result?.access_token;
      if (newToken) {
        dispatch(setAccessToken(newToken));
        const newUrl = `${baseUrl}/${station}/${newToken}`;
        latestSocketUrl.current = newUrl;

        reconnectDelay.current = 1000;
        shouldReconnect.current = true;

        connect(newUrl);
      } else {
        console.error("❌ Token refresh returned no token");
        shouldReconnect.current = false;
      }
    } catch (err) {
      console.error("❌ Token refresh failed:", err);
      shouldReconnect.current = false;
    }
  }, [station, dispatch, connect]);

  const forceReconnect = useCallback(() => {
    console.warn("🔄 Force reconnect triggered...");
    if (ws.current) {
      shouldReconnect.current = false;
      ws.current.close();
      ws.current = null;
    }

    clearInterval(pingIntervalId.current);
    clearTimeout(reconnectTimeoutId.current);

    reconnectDelay.current = 1000;
    shouldReconnect.current = true;

    if (!ws.current) {
      reconnectWithNewToken();
    }
  }, [reconnectWithNewToken]);

  const connect = useCallback(
    (url) => {
      if (ws.current) {
        console.warn("⚠️ WebSocket already exists. Skipping new connection.");
        return;
      }

      const socketInstance = new WebSocket(url);
      ws.current = socketInstance;
      setSocketInstance(socketInstance);

      socketInstance.addEventListener("open", () => {
        console.log("✅ WebSocket connected:", url);
        clearInterval(pingIntervalId.current);
        pingIntervalId.current = setInterval(() => {
          try {
            socketInstance.send(JSON.stringify({ type: "ping" }));
          } catch (e) {
            console.warn("⚠️ Ping failed:", e);
          }
        }, 55 * 1000);
        reconnectDelay.current = 1000;
      });

      socketInstance.addEventListener("message", (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data?.code === 401) {
            console.warn(
              "🚫 Received 401. Refreshing token and reconnecting..."
            );
            forceReconnect();
          }
        } catch (e) {
          console.error("⚠️ Message parse error", e);
        }
      });

      socketInstance.addEventListener("close", () => {
        console.warn("🔌 WebSocket closed");
        setSocketInstance(null);
        clearInterval(pingIntervalId.current);
        ws.current = null;

        if (shouldReconnect.current) {
          reconnectTimeoutId.current = setTimeout(() => {
            console.log("🔁 Reconnecting WebSocket...");
            connect(latestSocketUrl.current);
          }, reconnectDelay.current);
          reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000);
        }
      });

      socketInstance.addEventListener("error", (e) => {
        console.error("❗ WebSocket error:", e);
        socketInstance.close();
      });
    },
    [forceReconnect, connect]
  );

  useEffect(() => {
    return () => {
      shouldReconnect.current = false;
      clearInterval(pingIntervalId.current);
      clearTimeout(reconnectTimeoutId.current);
      ws.current?.close();
      ws.current = null;
      setSocketInstance(null);
    };
  }, []);

  return {
    ws: socketInstance,
    setStation,
    forceReconnect,
  };
};
